import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';

import { StopOutlined } from '@ant-design/icons';
import ChunkedVideoRecorder from '@components/shared/ChunkedVideoRecorder';
import type { ChunkedVideoRecorderHandle } from '@components/shared/ChunkedVideoRecorder';
import { QCFlowStates } from '@contexts/operator/qcFlowMachine';
import type { QCFlowState } from '@contexts/operator/qcFlowMachine';
import { VideoStopCountdownProvider, useVideoStopCountdown } from '@contexts/operator/VideoStopCountdownContext';
import type { Subtitle, SessionMeta } from '@hooks/operator/useChunkedVideoRecorder';
import type { ChunkTask } from '@hooks/operator/useChunkedUploadQueue';
import { Button, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const CountdownRow = styled.div`
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  gap: 8px;
  text-align: center;

  .countdown-tag {
    white-space: normal;
    margin: 0;
  }

  .countdown-stop-btn {
    width: 100%;
  }
`;

export interface VideoRecorderUploadQueue {
  enqueueChunk: (task: Omit<ChunkTask, 'type'>) => boolean;
}

export interface VideoRecorderHandle {
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<SessionMeta | null>;
  stopAndRestartRecording: () => Promise<SessionMeta | null>;
  cancelRecording: () => void;
}

export interface VideoRecorderProps {
  orderNumber: string;
  videoSubtitle?: Subtitle | null;
  autoStart?: boolean;
  qcFlowState: QCFlowState;
  currentQcFulfillment?: { id: number | string } | null;
  uploadQueue?: VideoRecorderUploadQueue | null;
  onStopAndFinalize?: () => void;
}

/**
 * QC 專用錄影外殼 — wrap @shared/ChunkedVideoRecorder，加上 QC 流程獨有的：
 *   - 結案後 60s 倒數 + 「結束錄影並上傳」按鈕
 *   - COMPLETED 狀態下隱藏 Pause / Resume（停止由倒數或手動結束統一接手）
 *   - autoStart 排除 COMPLETED（避免結案後又啟動新 session）
 *   - 卸載時若仍在 COMPLETED，best-effort 觸發 onStopAndFinalize
 *   - onChunk → uploadQueue.enqueueChunk(entityId=fulfillment.id)
 */
const QcVideoRecorderInner = React.forwardRef<VideoRecorderHandle, VideoRecorderProps>(({
  orderNumber,
  videoSubtitle,
  autoStart,
  qcFlowState,
  currentQcFulfillment,
  uploadQueue,
  onStopAndFinalize,
}, ref) => {
  const { t } = useTranslation();
  const { countdown, startCountdown, resetCountdown } = useVideoStopCountdown();
  const innerRef = useRef<ChunkedVideoRecorderHandle | null>(null);
  const [ isRecording, setIsRecording ] = useState(false);

  useImperativeHandle(ref, () => ({
    startRecording: async () => { await innerRef.current?.startRecording(); },
    stopRecording: async () => innerRef.current?.stopRecording() ?? null,
    stopAndRestartRecording: async () => innerRef.current?.stopAndRestartRecording() ?? null,
    cancelRecording: () => innerRef.current?.cancelRecording(),
  }), []);

  // COMPLETED 排除：autoStart 在結案後不該再 fire（避免結案後 stale prop 又啟動 session）
  const shouldAutoStart = !!autoStart && qcFlowState !== QCFlowStates.COMPLETED;

  // 結案後 60s 自動停 — 倒數本身、手動點按鈕、掃下一單三條觸發路徑統一回到 onStopAndFinalize。
  // 看到 isRecording 才啟動倒數（避免 mount 進到 COMPLETED 但根本沒在錄影的情境誤觸發）。
  useEffect(() => {
    if (qcFlowState === QCFlowStates.COMPLETED && isRecording) {
      startCountdown(() => {
        onStopAndFinalize?.();
      });
    } else if (qcFlowState !== QCFlowStates.COMPLETED) {
      resetCountdown();
    }
  }, [ qcFlowState, isRecording, startCountdown, resetCountdown, onStopAndFinalize ]);

  // Unmount safety net：在 COMPLETED 狀態下卸載組件時 best-effort finalize。
  // 用 ref 抓最新 prop，避免 effect cleanup 抓到 stale closure。
  const qcFlowStateRef = useRef(qcFlowState);
  const onStopAndFinalizeRef = useRef(onStopAndFinalize);
  useEffect(() => { qcFlowStateRef.current = qcFlowState; });
  useEffect(() => { onStopAndFinalizeRef.current = onStopAndFinalize; });
  useEffect(() => {
    return () => {
      if (qcFlowStateRef.current === QCFlowStates.COMPLETED) {
        onStopAndFinalizeRef.current?.();
      }
    };
  }, []);

  const handleChunk = (blob: Blob, seq: number, sessionId: string) => {
    if (currentQcFulfillment?.id && uploadQueue) {
      const accepted = uploadQueue.enqueueChunk({
        entityId: currentQcFulfillment.id,
        sessionId,
        seq,
        blob,
      });
      if (!accepted) {
        // Queue at cap — onQueueOverflow modal already shown by QcContext.
        // Stop the recorder so we don't keep dropping chunks (which would
        // make total_chunks mismatch what's in GCS).
        innerRef.current?.cancelRecording();
      }
    }
  };

  return (
    <ChunkedVideoRecorder
      ref={innerRef}
      orderNumber={orderNumber}
      subtitle={videoSubtitle}
      autoStart={shouldAutoStart}
      hidePauseControls={qcFlowState === QCFlowStates.COMPLETED}
      onChunk={handleChunk}
      onRecordingChange={setIsRecording}
    >
      {countdown !== null && qcFlowState === QCFlowStates.COMPLETED && (
        <CountdownRow>
          <Tag
            className="countdown-tag"
            color={countdown <= 10 ? 'error' : 'warning'}
            style={{
              fontSize: countdown <= 10 ? '15px' : '13px',
              fontWeight: 'bold',
              padding: '6px 12px',
              animation: countdown <= 10 ? 'countdownPulse 0.8s ease-in-out infinite' : 'none',
            }}
          >
            錄影將在 <span style={{ fontFamily: 'monospace', fontSize: countdown <= 10 ? '18px' : '15px', padding: '0 4px' }}>{countdown}</span> 秒後自動停止
          </Tag>
          <Button
            className="countdown-stop-btn"
            type="primary"
            danger
            icon={<StopOutlined />}
            onClick={() => onStopAndFinalize?.()}
          >
            {t('shared.video_recorder.stop_now', { defaultValue: '結束錄影並上傳' })}
          </Button>
        </CountdownRow>
      )}
    </ChunkedVideoRecorder>
  );
});

QcVideoRecorderInner.displayName = 'QcVideoRecorderInner';

const VideoRecorder = React.forwardRef<VideoRecorderHandle, VideoRecorderProps>((props, ref) => {
  return (
    <VideoStopCountdownProvider>
      <QcVideoRecorderInner {...props} ref={ref} />
    </VideoStopCountdownProvider>
  );
});

VideoRecorder.displayName = 'VideoRecorder';

export default VideoRecorder;
